import { create } from 'zustand';
import { persist, devtools } from 'zustand/middleware';
import type { User, Session } from '@supabase/supabase-js';
import { supabase } from '../lib/supabase';
import type { UserProfileDocType } from '../db/schemas';
import { initDatabase } from '../db/database';
import type { Subscription } from 'rxjs';

interface AuthState {
  user: User | null;
  session: Session | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isOwner: boolean; // Computed from profile.is_owner
  profile: UserProfileDocType | null;
  selectedBranchId: string | null;
  error: string | null;
  _profileSub: Subscription | null;

  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signOut: () => Promise<void>;
  setSelectedBranchId: (branchId: string | null) => void;
  clearError: () => void;
  _loadProfile: (userId: string) => Promise<void>;
}

export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        user: null,
        session: null,
        isAuthenticated: false,
        isLoading: true,
        isOwner: false,
        profile: null,
        selectedBranchId: null,
        error: null,
        _profileSub: null,

        _loadProfile: async (userId: string) => {
          try {
            // Cleanup previous sub first before creating new subscription
            const currentSub = get()._profileSub;
            if (currentSub) {
              currentSub.unsubscribe();
              set({ _profileSub: null });
            }

            const db = await initDatabase(userId);
            const sub = db.user_profiles.findOne({ selector: { id: userId } }).$.subscribe(doc => {
              if (doc) {
                const profile = doc.toJSON() as unknown as UserProfileDocType;
                const state = get();
                const defaultBranchId = state.selectedBranchId || (profile.assigned_branches?.length ? profile.assigned_branches[0] : null);
                set({ 
                  profile,
                  isOwner: profile.is_owner === true,
                  selectedBranchId: defaultBranchId
                });
              }
            });
            
            set({ _profileSub: sub });
          } catch (e) {
            console.error('Failed to load user profile from RxDB', e);
          }
        },

        initialize: async () => {
          if (!supabase) {
            // No Supabase configured — run in local-only mode
            set({
              isLoading: false,
              isAuthenticated: true,
              user: { id: 'local-user', email: 'local@depilaway.app' } as User,
              isOwner: true,
              selectedBranchId: 'default',
            });
            initDatabase('local');
            return;
          }

          try {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
              const isOwner = session.user.app_metadata?.role === 'owner';
              set({
                session,
                user: session.user,
                isAuthenticated: true,
                isOwner,
                isLoading: false,
              });
              get()._loadProfile(session.user.id);
            } else {
              set({ isLoading: false });
              initDatabase('local');
            }

            // Listen for auth changes
            supabase.auth.onAuthStateChange((_event, session) => {
              const isOwner = session?.user.app_metadata?.role === 'owner';
              set({
                session,
                user: session?.user ?? null,
                isAuthenticated: !!session,
                isOwner: isOwner ?? false,
              });
              if (session?.user) {
                get()._loadProfile(session.user.id);
              } else {
                const currentSub = get()._profileSub;
                if (currentSub) currentSub.unsubscribe();
                set({ profile: null, _profileSub: null, selectedBranchId: null });
                initDatabase('local');
              }
            });
          } catch {
            // Offline — use cached session
            const cached = get();
            if (cached.session) {
              set({ isLoading: false, isAuthenticated: true });
              get()._loadProfile(cached.session.user.id);
            } else {
              set({ isLoading: false });
              initDatabase('local');
            }
          }
        },

        signIn: async (email, password) => {
          if (!supabase) {
            set({ error: 'Supabase no configurado' });
            return;
          }
          set({ isLoading: true, error: null });
          const { data, error } = await supabase.auth.signInWithPassword({ email, password });
          if (error) {
            set({ error: error.message, isLoading: false });
          } else if (data.session) {
            const isOwner = data.session.user.app_metadata?.role === 'owner';
            set({
              session: data.session,
              user: data.session.user,
              isAuthenticated: true,
              isOwner,
              isLoading: false,
            });
            get()._loadProfile(data.session.user.id);
          }
        },

        signOut: async () => {
          if (supabase) {
            await supabase.auth.signOut();
          }
          set({
            user: null,
            session: null,
            isAuthenticated: false,
            isOwner: false,
            profile: null,
            selectedBranchId: null,
          });
          const currentSub = get()._profileSub;
          if (currentSub) currentSub.unsubscribe();
          set({ _profileSub: null });
        },

        setSelectedBranchId: (branchId: string | null) => set({ selectedBranchId: branchId }),

        clearError: () => set({ error: null }),
      }),
      {
        name: 'depilaway-auth',
        partialize: (state) => ({
          session: state.session,
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          isOwner: state.isOwner,
          profile: state.profile,
          selectedBranchId: state.selectedBranchId,
        }),
      }
    ),
    { name: 'AuthStore' }
  )
);
